import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuthContext } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';

interface Payment {
  id: string;
  amount: number;
  status: string;
  dueDate: string;
  invoiceNumber?: string;
  description?: string;
  payabliToken?: string;
  payabliEntryId?: string;
  currency?: string;
  paymentType?: string;
  createdAt?: string;
  updatedAt?: string;
  statementDate?: string;
  paymentDate?: string;
  paymentIdentity?: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string;
    lastName: string;
    uniqueId: string;
    guarantorLastName: string;
  };
  totalDueAmount?: number;
}

interface UserInfo {
  name: string;
  gn: string;
}

interface PaymentFormProps {
  payment: Payment;
  userInfo: UserInfo;
  isScheduledPayment: boolean;
  setIsScheduledPayment: (value: boolean) => void;
  scheduleFrequency: string;
  setScheduleFrequency: (value: string) => void;
  splitType: string;
  setSplitType: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  numberOfPayments: number;
  setNumberOfPayments: (value: number) => void;
  amountPerPayment: string;
  setAmountPerPayment: (value: string) => void;
  endDate: string;
  isScheduledPaymentValid: () => boolean;
  loadPayabliScript: (payment: Payment) => void;
  getMinStartDate: () => string;
  SCHEDULE_FREQUENCIES: any;
  SPLIT_TYPES: any;
  isPayButtonVisible?: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  payment,
  userInfo,
  isScheduledPayment,
  setIsScheduledPayment,
  scheduleFrequency,
  setScheduleFrequency,
  splitType,
  setSplitType,
  startDate,
  setStartDate,
  numberOfPayments,
  setNumberOfPayments,
  amountPerPayment,
  setAmountPerPayment,
  endDate,
  isScheduledPaymentValid,
  loadPayabliScript,
  getMinStartDate,
  SCHEDULE_FREQUENCIES,
  SPLIT_TYPES,
  isPayButtonVisible = true
}) => {
  const { theme } = useTheme();
  const { isAuthenticated } = useAuthContext();
  const location = useLocation();
  const isPaymentAlreadyDone = payment.status?.toLowerCase() == "success";
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  
  const handleScheduledPaymentChange = (checked: boolean) => {
    if (checked && !isAuthenticated) {
      setShowLoginPopup(true);
    } else {
      setIsScheduledPayment(checked);
    }
  };

  const closeLoginPopup = () => {
    setShowLoginPopup(false);
  };

  const handleLoginRedirect = () => {
    // Save current URL to localStorage
    const currentUrl = window.location.pathname + window.location.search;
    localStorage.setItem('redirectAfterLogin', currentUrl);
    
    // Redirect to login page
    window.location.href = '/login';
    closeLoginPopup();
  };

  return (
    <div className="space-y-6">
      {/* Payment Details */}
      <div>
        <label className="block text-gray-800 text-sm font-bold mb-3">Bill Details</label>
        <div className="rounded-lg pb-2">
          {[
            ["Total Due", `$${payment.amount || 0}`],
            ["Paid", `$${payment.amount - (payment.totalDueAmount || 0)}`],
            ["Pending", `$${payment.totalDueAmount || 0}`],
            ["Invoice Number", payment.invoiceNumber || payment.id],
            ["Status", payment.status?.toUpperCase() || "N/A"],
            ["Due Date", new Date(payment.dueDate).toLocaleDateString()],
            ["Patient Name", `${payment.paymentIdentity?.firstName || ''} ${payment.paymentIdentity?.lastName || ''}`.trim() || userInfo?.name || "N/A"],
            ["Patient Email", payment.paymentIdentity?.email || "N/A"],
            ["Patient Phone", payment.paymentIdentity?.phone || "N/A"],
          ].map(([label, value]) => (
            <div key={label} className="flex text-sm font-semibold mb-2">
              <span className="w-40 text-gray-800">{label}:</span>
              <span className="text-gray-500 flex-1">{value || "N/A"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scheduled Payment Option */}
      {isPayButtonVisible && <div className="mb-4 mt-4">
        <label className="scheduled-payment-checkbox">
          <input
            type="checkbox"
            checked={isScheduledPayment}
            onChange={(e) => handleScheduledPaymentChange(e.target.checked)}
            disabled={isPaymentAlreadyDone}
            className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${isPaymentAlreadyDone ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
          <span className={isPaymentAlreadyDone ? 'text-gray-500' : ''}>Set up scheduled payments</span>
        </label>
      </div>}

      {/* Login Popup */}
      {showLoginPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Login Required</h2>
            <p className="mb-6">You need to login to schedule payments.</p>
            <div className="flex justify-end space-x-3">
              <button 
                onClick={closeLoginPopup}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleLoginRedirect}
                className={`px-4 py-2 rounded-md text-white ${theme["button-bg"]}`}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduled Payment Form */}
      {isScheduledPayment && (
        <div className="scheduled-payment-form rounded-lg p-4 mb-4 bg-white border border-gray-200 payment-form-animate">
          {/* Frequency Selection */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">Payment Frequency</label>
            <select
              value={scheduleFrequency}
              onChange={(e) => setScheduleFrequency(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={SCHEDULE_FREQUENCIES.WEEKLY}>Weekly</option>
              <option value={SCHEDULE_FREQUENCIES.BIWEEKLY}>Once in 2 weeks</option>
              <option value={SCHEDULE_FREQUENCIES.MONTHLY}>Monthly</option>
              <option value={SCHEDULE_FREQUENCIES.QUARTERLY}>Quarterly</option>
            </select>
          </div>

          {/* Split Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">Split Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                className={`flex-1 py-2 px-3 rounded border text-sm font-medium transition-smooth ${
                  splitType === SPLIT_TYPES.EQUAL
                    ? `${theme["button-bg"]} text-white`
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setSplitType(SPLIT_TYPES.EQUAL)}
              >
                Equal
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-3 rounded border text-sm font-medium transition-smooth ${
                  splitType === SPLIT_TYPES.MANUAL
                    ? `${theme["button-bg"]} text-white`
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setSplitType(SPLIT_TYPES.MANUAL)}
              >
                Pick your own amount
              </button>
            </div>
          </div>

          {/* Start Date */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={getMinStartDate()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Number of Payments - Only for Equal Split */}
          {splitType === SPLIT_TYPES.EQUAL && (
            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-700 mb-2">Number of Payments</label>
              <input
                type="number"
                value={numberOfPayments}
                onChange={(e) => setNumberOfPayments(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                max="52"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          )}
        </div>
      )}

      {/* Pay Button */}
      {isPayButtonVisible && <button
        onClick={() => loadPayabliScript(payment)}
        disabled={!isScheduledPaymentValid() || isPaymentAlreadyDone}
        className={`w-full ${theme["button-bg"]} text-white text-lg font-semibold rounded-lg px-6 py-3 ${theme["home-button-text"]} button-hover ${
          !isScheduledPaymentValid() || isPaymentAlreadyDone ? 'opacity-60 cursor-not-allowed' : ''
        }`}
      >
        {isScheduledPayment ? 
          `Schedule ${splitType === SPLIT_TYPES.EQUAL ? 'Equal' : 'Manual'} Payment ` : 
          'Pay '
        }${payment.amount}
      </button>}

      {isPaymentAlreadyDone && (
        <div className="text-center border justify-center items-center flex text-center border-green-200 bg-green-50 pt-2 pb-2 rounded-lg mt-4">
         <p className="text-lg text-green-600">Your payment was already successfully processed.</p>
       </div>
      )}

      {/* Payment Support Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-600 text-center mb-4">We accept the following payment methods</p>
        <div className="flex justify-center items-center gap-4 flex-wrap">
          <img src="/Images/visa.svg" alt="Visa" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
          <img src="/Images/mastercard.svg" alt="Mastercard" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
          <img src="/Images/amex.svg" alt="American Express" className="h-6 opacity-70 hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </div>
  );
};

export default PaymentForm; 